import ImageAsset from '@/editor/assets/image.asset'

const now = () => (typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now())

export default class GifAsset extends ImageAsset {
  constructor(uuid) {
    super(uuid)
    this._type = 'image'
    this._gif = {
      decodedAt: null,
      frameCount: 0,
      duration: 0,
      frames: [], // ImageBitmap[]
      frameDurations: [], // seconds[]
      frameStarts: [], // seconds[] (cumulative)
      repetitionCount: null,
      width: null,
      height: null
    }
  }

  get isAnimated() {
    return true
  }

  get frameCount() {
    return this._gif.frameCount || 0
  }

  get duration() {
    return this._gif.duration || 0
  }

  get frames() {
    return this._gif.frames || []
  }

  /**
   * Given clip-local time in seconds, return the GIF frame index.
   * Loop-to-fill behavior: time wraps across intrinsic GIF duration.
   */
  frameIndexAt(localTime) {
    const total = Number(this._gif.duration) || 0
    const n = Number(this._gif.frameCount) || 0
    if (!n) return 0
    if (!total || total <= 0) return 0

    const t = Math.max(0, Number(localTime) || 0)
    const looped = t % total

    const starts = this._gif.frameStarts || []
    const durs = this._gif.frameDurations || []
    for (let i = 0; i < n; i++) {
      const s = Number(starts[i]) || 0
      const d = Number(durs[i]) || 0
      if (looped >= s && looped < s + d) return i
    }
    return Math.max(0, n - 1)
  }

  async loadFromFile(file) {
    await this._loadFromFile(file)
    this._asset.meta.name = file.name
    await this.decodeGif()
    this._asset.ready = true
    this.emit('ready')
  }

  updateFrame(time, localTime, frame, zf, isPlay, propsUp) {
    // Preserve normal ImageAsset (D3Asset) behavior: visibility + transforms + opacity
    const props = super.updateFrame(time, localTime, frame, zf, isPlay, propsUp)

    if (!frame || !frame.mesh || !frame.strip) return props
    if (localTime < 0 || localTime > frame.strip.length) return props

    const canvas = frame._gifCanvas
    const ctx = frame._gifCtx
    const tex = frame._gifTexture
    if (!canvas || !ctx || !tex) return props

    const idx = this.frameIndexAt(localTime)
    if (idx === frame._gifLastFrameIndex) return props

    const bmp = this.frames[idx] || null
    if (!bmp) return props

    // Keep canvas size aligned to current asset meta (or bitmap size as fallback)
    const w = Number(this._asset.meta?.width) || bmp.width || canvas.width || 1
    const h = Number(this._asset.meta?.height) || bmp.height || canvas.height || 1
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w
      canvas.height = h
    }

    try {
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(bmp, 0, 0, canvas.width, canvas.height)
      frame._gifLastFrameIndex = idx
      tex.needsUpdate = true
    } catch (e) {
      // If draw fails, don't lock the index so we can retry next tick.
      frame._gifLastFrameIndex = -1
    }

    return props
  }

  _disposeFrames() {
    const frames = this._gif.frames || []
    frames.forEach((bmp) => {
      try { bmp.close?.() } catch (e) { /* noop */ }
    })
    this._gif.frames = []
  }

  dispose() {
    this._disposeFrames()
    try {
      if (typeof window !== 'undefined' && window.__vybyGifAssets) {
        delete window.__vybyGifAssets[this.uuid]
      }
    } catch (e) { /* noop */ }
  }

  /**
   * Decode GIF frames using WebCodecs ImageDecoder (Chrome).
   * Stores frames as ImageBitmap and frame durations in seconds.
   */
  async decodeGif() {
    this._disposeFrames()

    const blob = this._asset.blob
    const type = (this._asset.mime || blob?.type || 'image/gif') + ''
    const supported = typeof window !== 'undefined' && typeof window.ImageDecoder === 'function'
    if (!supported) {
      // Chrome-only phase; still keep basic image metadata so the editor continues working.
      await super.loadInfo()
      this._asset.meta = {
        ...this._asset.meta,
        animated: true,
        format: 'gif',
        duration: this._asset.meta.duration || 0,
        frameCount: 0,
      }
      return
    }

    const data = blob && typeof blob.stream === 'function'
      ? blob.stream()
      : new Response(blob).body

    const decoder = new window.ImageDecoder({ data, type })
    try {
      if (decoder.tracks && decoder.tracks.ready) {
        await decoder.tracks.ready
      }
    } catch (e) { /* noop */ }

    const selected = decoder.tracks?.selectedTrack || null
    const frameCount = Number(selected?.frameCount)
    const repetitionCount = (typeof selected?.repetitionCount === 'number') ? selected.repetitionCount : null

    // Timing: many GIFs use 10–20ms per frame; don't clamp upward or you'll slow playback.
    const DEFAULT_FRAME_SEC = 0.1
    const MIN_FRAME_SEC = 0.02
    const MAX_FRAME_SEC = 10

    // Safety caps to avoid locking up on massive GIFs (Chrome-only Phase 3).
    const MAX_FRAMES = 600
    const MAX_TOTAL_MP = 400 // total megapixels across decoded frames (e.g. 400MP ~= 400 * 1e6 pixels)
    const frames = []
    const frameDurations = []
    const frameStarts = []
    let total = 0
    let width = null
    let height = null
    let truncated = false

    const count = Number.isFinite(frameCount) && frameCount > 0 ? frameCount : 1
    const decodeCount = Math.min(count, MAX_FRAMES)
    for (let i = 0; i < decodeCount; i++) {
      // decode() returns { image: VideoFrame, complete: boolean }
      // For animated images, VideoFrame.duration is expected to be set (microseconds).
      const res = await decoder.decode({ frameIndex: i })
      const vf = res && res.image ? res.image : null
      if (!vf) continue

      if (!width) width = vf.displayWidth || vf.codedWidth || null
      if (!height) height = vf.displayHeight || vf.codedHeight || null

      const durUs = (typeof vf.duration === 'number' && Number.isFinite(vf.duration) && vf.duration > 0)
        ? vf.duration
        : Math.round(DEFAULT_FRAME_SEC * 1e6)
      let durSec = durUs / 1e6
      if (!Number.isFinite(durSec) || durSec <= 0) durSec = DEFAULT_FRAME_SEC
      durSec = Math.max(MIN_FRAME_SEC, Math.min(MAX_FRAME_SEC, durSec))

      frameStarts.push(total)
      frameDurations.push(durSec)
      total += durSec

      const bmp = await createImageBitmap(vf)
      frames.push(bmp)
      try { vf.close?.() } catch (e) { /* noop */ }

      // Total-pixel cap check (after first frame dims known)
      try {
        const w = Number(width) || bmp.width || 0
        const h = Number(height) || bmp.height || 0
        const totalPixels = frames.length * w * h
        if (w > 0 && h > 0 && totalPixels > MAX_TOTAL_MP * 1e6) {
          truncated = true
          break
        }
      } catch (e) { /* noop */ }
    }
    if (decodeCount < count) truncated = true

    // Thumbnail: PNG first frame to avoid animated thumbs in the library grid.
    try {
      if (frames[0] && typeof document !== 'undefined') {
        const canvas = document.createElement('canvas')
        const w = Number(width) || frames[0].width || 1
        const h = Number(height) || frames[0].height || 1
        canvas.width = w
        canvas.height = h
        const ctx = canvas.getContext('2d')
        if (ctx) ctx.drawImage(frames[0], 0, 0, w, h)
        const thumb = await new Promise((resolve) => {
          canvas.toBlob((b) => resolve(b || null), 'image/png')
        })
        if (thumb) this._asset.thumbnail = thumb
      }
    } catch (e) { /* noop */ }

    try { decoder.close?.() } catch (e) { /* noop */ }

    this._gif.decodedAt = now()
    this._gif.frameCount = frames.length
    this._gif.duration = total
    this._gif.frames = frames
    this._gif.frameDurations = frameDurations
    this._gif.frameStarts = frameStarts
    this._gif.repetitionCount = repetitionCount
    this._gif.width = width
    this._gif.height = height

    this._asset.meta = {
      ...this._asset.meta,
      width: Number(width) || this._asset.meta.width,
      height: Number(height) || this._asset.meta.height,
      animated: true,
      format: 'gif',
      frameCount: frames.length,
      duration: total,
      repetitionCount,
      gifTruncated: truncated
    }

    // Optional debug surface
    try {
      if (typeof window !== 'undefined') {
        window.__vybyGifAssets = window.__vybyGifAssets || {}
        window.__vybyGifAssets[this.uuid] = {
          uuid: this.uuid,
          frameCount: frames.length,
          duration: total,
          repetitionCount,
          width: Number(width) || null,
          height: Number(height) || null,
          decodedAt: this._gif.decodedAt,
          gifTruncated: truncated
        }
        if (!window.__vybyGifDebug) {
          window.__vybyGifDebug = (uuid = null) => {
            const map = window.__vybyGifAssets || {}
            if (!uuid) return map
            return map[uuid] || null
          }
        }
      }
    } catch (e) { /* noop */ }
  }
}


